'use client'

import { Box, Group, Stack, Text } from '@mantine/core'
import { DataTable, DataTableColumn } from 'mantine-datatable'
import { useTranslations } from 'next-intl'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'

import EmptyState from '@/app/[locale]/price/categories/_components/CategoryTable/Table/EmptyState'
import NextBaseIcon from '@/components/basic/BaseIcon'
import NextBaseImage from '@/components/basic/BaseImage'
import { SectionErrorBoundary } from '@/components/error/boundary'
import PriceChange from '@/components/PriceChange'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { I18nLink, useI18nRouter } from '@/i18n/navigation'
import { CoinCategory } from '@/services/category'
import { formatMarketCap } from '@/utils/number/format'

import sharedStyles from '../style.module.css'
import styles from './style.module.css'

const TOP_COINS_LIMIT = 2

function NameCell({ category }: { category: CoinCategory }) {
  const { image_url, name, slug, percent_change_24h_native, highlighted_coins: topCoins } = category
  const router = useI18nRouter()
  const mobileCoins = (topCoins ?? []).slice(0, TOP_COINS_LIMIT)
  const coinsRowRef = useRef<HTMLDivElement>(null)

  const [hasOverflow, setHasOverflow] = useState(false)
  useLayoutEffect(() => {
    const el = coinsRowRef.current
    if (!el) return
    const update = () => setHasOverflow(el.scrollWidth > el.clientWidth + 1)
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <Group gap={8} wrap="nowrap" align="center">
      <Box className={styles.categoryImage}>
        <NextBaseImage src={image_url} width="100%" height="100%" radius="inherit" alt={name} />
      </Box>

      <div className={styles.mobileCategoryInfo}>
        <Group gap={8} wrap="nowrap">
          <I18nLink href={`/price/categories/${slug}`} className={styles.categoryName}>
            <Text variant="body">{name}</Text>
          </I18nLink>
          <Box className={sharedStyles.mobilePriceChange}>
            <PriceChange value={Number(percent_change_24h_native) * 0.01} />
          </Box>
        </Group>

        <div
          ref={coinsRowRef}
          className={`${styles.mobileCoinsRow} ${hasOverflow ? styles.mobileCoinsRowFaded : ''}`}
        >
          {mobileCoins.map(coin => (
            <button
              key={coin.id}
              type="button"
              className={styles.mobileCoin}
              onClick={e => {
                e.stopPropagation()
                if (coin.slug) {
                  router.push(`/${coin.slug}`)
                }
              }}
            >
              <NextBaseIcon
                src={coin.icon_image_url}
                size={16}
                circle
                fallbackText={coin.name}
                alt={coin.name}
              />
              <Text component="span" fz={12} lh="16px" truncate style={{ maxWidth: 90 }}>
                {coin.symbol}
              </Text>
              <PriceChange
                fz={12}
                lh="16px"
                value={Number(coin.percent_change_24h_native) * 0.01}
              />
            </button>
          ))}
        </div>
      </div>
    </Group>
  )
}

function MarketCapCell({ category }: { category: CoinCategory }) {
  const {
    amounts: [{ formattedValue }],
  } = useCurrencyAmounts({
    amounts: [Number(category.market_cap_native.amount)],
    valueFormatter: formatMarketCap,
    currencyFormatOptions: {},
  })

  return <Text variant="body1">{formattedValue}</Text>
}

function VolumeCell({ category }: { category: CoinCategory }) {
  const {
    amounts: [{ formattedValue }],
  } = useCurrencyAmounts({
    amounts: [Number(category.volume_24h_native.amount)],
    valueFormatter: formatMarketCap,
    currencyFormatOptions: {},
  })

  return <Text variant="body1">{formattedValue}</Text>
}

function TopCoinsCell({ category }: { category: CoinCategory }) {
  const { highlighted_coins: topCoins } = category
  const router = useI18nRouter()

  return (
    <Stack gap={12} align="flex-start">
      {topCoins?.slice(0, TOP_COINS_LIMIT).map(coin => (
        <Group
          key={coin.id}
          gap={8}
          wrap="nowrap"
          justify="flex-start"
          className={styles.topCoinsItem}
          role="button"
          tabIndex={0}
          onClick={e => {
            e.stopPropagation()
            router.push(`/${coin.slug}`)
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.stopPropagation()
              e.preventDefault()
              router.push(`/${coin.slug}`)
            }
          }}
        >
          <NextBaseIcon
            src={coin.icon_image_url}
            size={20}
            circle
            fallbackText={coin.name}
            alt={coin.name}
          />
          <Text variant="body1" truncate className={styles.coinName}>
            {coin.symbol}
          </Text>
          <PriceChange w={80} value={Number(coin.percent_change_24h_native) * 0.01} />
        </Group>
      ))}
    </Stack>
  )
}

type CategorySortKey = 'market_cap_native' | 'volume_24h_native' | 'percent_change_24h_native'

function getCategorySortValue(category: CoinCategory, key: CategorySortKey): number {
  if (key === 'percent_change_24h_native') {
    return Number(category.percent_change_24h_native) || 0
  }
  return Number(category[key]?.amount) || 0
}

interface CategoriesTableProps {
  data: CoinCategory[]
  sortKey?: CategorySortKey
}

export default function CategoriesTable({
  data,
  sortKey = 'volume_24h_native',
}: CategoriesTableProps) {
  const t = useTranslations()
  const router = useI18nRouter()

  const sortedCategories = useMemo(
    () =>
      [...(data ?? [])].sort(
        (a, b) => getCategorySortValue(b, sortKey) - getCategorySortValue(a, sortKey),
      ),
    [data, sortKey],
  )

  const columns = useMemo<DataTableColumn<CoinCategory>[]>(
    () => [
      {
        accessor: 'name',
        title: t('category.category'),
        textAlign: 'left',
        width: 240,
        render: (category: CoinCategory) => <NameCell category={category} />,
      },
      {
        accessor: 'market_cap_native',
        title: t('HomePageV2.coinsTable.marketCap'),
        textAlign: 'right',
        width: '18%',
        render: (category: CoinCategory) => <MarketCapCell category={category} />,
      },
      {
        accessor: 'volume_24h_native',
        title: t('HomePageV2.coinsTable.24hVolume'),
        textAlign: 'right',
        width: '18%',
        render: (category: CoinCategory) => <VolumeCell category={category} />,
      },
      {
        accessor: 'percent_change_24h_native',
        title: t('HomePageV2.coinsTable.24hChange'),
        textAlign: 'right',
        width: '18%',
        render: (category: CoinCategory) => (
          <PriceChange value={Number(category.percent_change_24h_native) * 0.01} />
        ),
      },
      {
        accessor: 'highlighted_coins',
        title: t('HomePageV2.categories.topCoins'),
        textAlign: 'left',
        width: '20%',
        render: (category: CoinCategory) => <TopCoinsCell category={category} />,
      },
    ],
    [t],
  )

  if (sortedCategories.length === 0) {
    return <EmptyState />
  }

  return (
    <SectionErrorBoundary>
      <Box pos="relative">
        <Stack gap="16px">
          <DataTable
            textSelectionDisabled
            records={sortedCategories}
            columns={columns}
            defaultColumnProps={{
              titleClassName: 'typography-body',
              cellsClassName: `typography-body-number ${sharedStyles.tableCell}`,
            }}
            idAccessor="id"
            pinFirstColumn
            onRowClick={({ record }: { record: CoinCategory }) => {
              if (record.slug) {
                router.push(`/categories/${record.slug}`)
              }
            }}
            classNames={{ root: styles.categoriesRoot, header: sharedStyles.header }}
            minHeight="500px"
            backgroundColor="var(--bg-app)"
            withTableBorder={false}
            withColumnBorders={false}
            withRowBorders={false}
            highlightOnHover
            highlightOnHoverColor="var(--bg-surface-secondary)"
          />
        </Stack>
      </Box>
    </SectionErrorBoundary>
  )
}
